import { useFormContext } from '@components/form';

import { getStringValue, isFieldMappedValue } from './dynamic-options-utils';
import { useDynamicListOptions } from './list-hooks';

import type { DynamicListOption, IntegrationEndpointListVariable } from '../integrations-types';

type Result = {
  isDynamic: boolean;
  hasAllParameters: boolean;
  missingFields: string[];
  options: DynamicListOption[];
  isLoading: boolean;
  isFetched: boolean;
  parametersKey: string;
};

type Options = {
  getFieldId: (variableName: string) => string;
  getFieldLabel?: (variableName: string) => string;
  getInitialValue?: (variableName: string) => string | undefined;
};

// For a dynamic-list endpoint field, look up each parameter's value from another field
// in the same form (via the caller-provided lookups) and fetch the list once everything is filled.
export const useDynamicEndpointOptions = (
  field: IntegrationEndpointListVariable,
  { getFieldId, getFieldLabel, getInitialValue }: Options,
): Result => {
  const { store } = useFormContext();
  const parameterMap = field.parameter_map ?? {};
  const isDynamic = field.options_from !== undefined && Object.keys(parameterMap).length > 0;

  const formValues = store.useFormValues();
  const parameters: Record<string, string> = {};
  const missingFields: string[] = [];
  for (const [parameterName, variableName] of Object.entries(parameterMap)) {
    const formValue = formValues[getFieldId(variableName)];
    const value = getStringValue(formValue) ?? getInitialValue?.(variableName);
    if (value) {
      parameters[parameterName] = value;
    } else if (!isFieldMappedValue(formValue)) {
      // Field-mapped parameters can't be resolved at config-time but they're
      // not missing either — only flag truly empty fields.
      missingFields.push(getFieldLabel?.(variableName) ?? variableName);
    }
  }

  const hasAllParameters = Object.keys(parameters).length === Object.keys(parameterMap).length;
  const { options, isLoading, isFetched } = useDynamicListOptions(
    field.options_from,
    parameters,
    isDynamic && hasAllParameters,
  );

  return {
    isDynamic,
    hasAllParameters,
    missingFields,
    options,
    isLoading,
    isFetched,
    parametersKey: JSON.stringify(parameters),
  };
};
